import useWindowEvent, { dispatchWindowEvent } from './useWindowEvent';

export const refreshEvents = {
  accounts: 'accounts',
  actionItem: 'action-item',
  modalItemDeleted: 'modalItemDeleted',
  modalCollectionCleared: 'modalCollectionCleared',
  authenticationFactors: 'authentication-factors',
  goals: 'goals',
  manualRoutingSubmitted: 'manualRoutingSubmitted',
  customInsightUpdated: 'customInsightUpdated',
  meetingContext: 'meetingContext',
  missionControlLiveEmail: 'mission-control-live-email',
  thread: 'thread',
  meetingBooked: 'meetingBooked',
  threadList: 'threadList',
  accomplishmentsTab: 'accomplishmentsTab',
  selectedAllocationHistory: 'selectedAllocationHistory',
  meetingSummary: 'meetingSummary',
  onboardingNavigation: 'onboardingNavigation',
  managedAccountsNavigationList: 'managedAccountsNavigationList',
  modalContextSubmissionSuccessful: 'modalContextSubmissionSuccessful',
  profileImageChanged: 'profileImageChanged',
  rightSidebarUpdated: 'rightSidebarUpdated',
  balanceSheetSidebarUpdated: 'balanceSheetSidebarUpdated',
  dashboardContentUpdated: 'dashboardContentUpdated',
  nextUpItemDetailUpdated: 'nextUpItemDetailUpdated',
  desiredOutcomesSidebarUpdated: 'desiredOutcomesSidebarUpdated',
  automationSuggestionsSidebarUpdated: 'automationSuggestionsSidebarUpdated',
  accountStatusChanged: 'accountStatusChanged',
  plaidStart: 'plaidStart',
  plaidExit: 'plaidExit',
  wizardFinished: 'wizardFinished',
};

function buildKey(evtName) {
  return `refresh-${evtName}`;
}

// Senders
function dispatchRefreshEvent(eventName, data) {
  dispatchWindowEvent(buildKey(eventName), data);
}

export const dispatchAccountsRefresh = () => dispatchRefreshEvent(refreshEvents.accounts);
export const dispatchActionItemRefresh = () => dispatchRefreshEvent(refreshEvents.actionItem);
export const dispatchModalItemDeletedRefresh = () => dispatchRefreshEvent(refreshEvents.modalItemDeleted);
export const dispatchModalCollectionCleared = (modalCollection) => dispatchRefreshEvent(refreshEvents.modalCollectionCleared, { modalCollection });
export const dispatchWizardFinishedRefresh = () => dispatchRefreshEvent(refreshEvents.wizardFinished);
export const dispatchPlaidStart = () => dispatchRefreshEvent(refreshEvents.plaidStart);
export const dispatchPlaidExit = () => dispatchRefreshEvent(refreshEvents.plaidExit);
export const dispatchAuthenticationFactorsRefresh = () => dispatchRefreshEvent(refreshEvents.authenticationFactors);
export const dispatchGoalsRefresh = () => dispatchRefreshEvent(refreshEvents.goals);
export const dispatchMissionControlLiveRefresh = () => dispatchRefreshEvent(refreshEvents.missionControlLiveEmail);
export const dispatchProfileImageChanged = () => dispatchRefreshEvent(refreshEvents.profileImageChanged);
export const dispatchMeetingContextRefresh = () => dispatchRefreshEvent(refreshEvents.meetingContext);
export const dispatchMeetingSummaryRefresh = () => dispatchRefreshEvent(refreshEvents.meetingSummary);
export const dispatchThreadRefresh = () => dispatchRefreshEvent(refreshEvents.thread);
export const dispatchManualRoutingSubmit = () => dispatchRefreshEvent(refreshEvents.manualRoutingSubmitted);
export const dispatchThreadListRefresh = () => dispatchRefreshEvent(refreshEvents.threadList);
export const dispatchAccomplishmentsTabRefresh = () => dispatchRefreshEvent(refreshEvents.accomplishmentsTab);
export const dispatchOnboardingNavigationRefresh = () => dispatchRefreshEvent(refreshEvents.onboardingNavigation);
export const dispatchManagedAccountsNavigationListRefresh = () => dispatchRefreshEvent(refreshEvents.managedAccountsNavigationList);
export const dispatchAccountStatusChangedRefresh = () => dispatchRefreshEvent(refreshEvents.accountStatusChanged);
export const dispatchModalContextSubmissionSuccessful = (resp, args = {}) => dispatchRefreshEvent(refreshEvents.modalContextSubmissionSuccessful, { ...args, response: resp });
export const dispatchRightSidebarUpdated = () => dispatchRefreshEvent(refreshEvents.rightSidebarUpdated);
export const dispatchBalanceSheetSidebarUpdated = () => dispatchRefreshEvent(refreshEvents.balanceSheetSidebarUpdated);
export const dispatchNextUpItemDetailUpdated = () => dispatchRefreshEvent(refreshEvents.nextUpItemDetailUpdated);
export const dispatchDashboardContentUpdated = () => dispatchRefreshEvent(refreshEvents.dashboardContentUpdated);
export const dispatchCustomInsightUpdated = () => dispatchRefreshEvent(refreshEvents.customInsightUpdated);
export const dispatchMeetingBooked = () => dispatchRefreshEvent(refreshEvents.meetingBooked);

// Receivers
function getRefreshEventReceiver(eventName) {
  // eslint-disable-next-line func-names
  return function (onRefresh, dependencies = []) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useWindowEvent(buildKey(eventName), onRefresh, dependencies);
  };
}

export const useMeetingBooked = getRefreshEventReceiver(refreshEvents.meetingBooked);
export const useRefreshAccounts = getRefreshEventReceiver(refreshEvents.accounts);
export const useRefreshActionItem = getRefreshEventReceiver(refreshEvents.actionItem);
export const useRefreshModalItemDeleted = getRefreshEventReceiver(refreshEvents.modalItemDeleted);
export const useRefreshModalCollectionCleared = getRefreshEventReceiver(refreshEvents.modalCollectionCleared);
export const useWizardFinished = getRefreshEventReceiver(refreshEvents.wizardFinished);
export const useRefreshAuthenticationFactors = getRefreshEventReceiver(refreshEvents.authenticationFactors);
export const useProfileImageChanged = getRefreshEventReceiver(refreshEvents.profileImageChanged);
export const useRefreshGoals = getRefreshEventReceiver(refreshEvents.goals);
export const useRefreshMissionControLiveEmail = getRefreshEventReceiver(refreshEvents.missionControlLiveEmail);
export const useRefreshMeetingContext = getRefreshEventReceiver(refreshEvents.meetingContext);
export const useRefreshMeetingSummary = getRefreshEventReceiver(refreshEvents.meetingSummary);
export const useRefreshOnboardingNavigation = getRefreshEventReceiver(refreshEvents.onboardingNavigation);
export const useAccomplishmentsTabContext = getRefreshEventReceiver(refreshEvents.accomplishmentsTab);
export const useRefreshManagedAccountsNavigationList = getRefreshEventReceiver(refreshEvents.managedAccountsNavigationList);
export const useRefreshedRightSidebar = getRefreshEventReceiver(refreshEvents.rightSidebarUpdated);
export const useModalContextSubmissionSuccessful = getRefreshEventReceiver(refreshEvents.modalContextSubmissionSuccessful);
export const useRightSidebarUpdated = getRefreshEventReceiver(refreshEvents.rightSidebarUpdated);
export const useNextUpItemDetailUpdated = getRefreshEventReceiver(refreshEvents.nextUpItemDetailUpdated);
export const useRefreshSelectedAllocationHistory = getRefreshEventReceiver(refreshEvents.selectedAllocationHistory);
export const useRefreshDashboardContent = getRefreshEventReceiver(refreshEvents.dashboardContentUpdated);
export const useRefreshBalanceSheetSidebar = getRefreshEventReceiver(refreshEvents.balanceSheetSidebarUpdated);
export const useRefreshAccountStatusChanged = getRefreshEventReceiver(refreshEvents.accountStatusChanged);
export const useRefreshDesiredOutcomesSidebar = getRefreshEventReceiver(refreshEvents.desiredOutcomesSidebarUpdated);
export const useAutomationSuggestionsSidebar = getRefreshEventReceiver(refreshEvents.automationSuggestionsSidebarUpdated);
export const usePlaidStart = getRefreshEventReceiver(refreshEvents.plaidStart);
export const usePlaidExit = getRefreshEventReceiver(refreshEvents.plaidExit);
export const useManualRoutingSubmit = getRefreshEventReceiver(refreshEvents.manualRoutingSubmitted);
export const useCustomInsightUpdated = getRefreshEventReceiver(refreshEvents.customInsightUpdated);
